import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CommonScrollBar from "../../Common/CommonScrollBar";
import CommonDateTime from "../../Common/CommonDateTime";
import CheckValid from "../../Common/CheckValid";
import EmptySpaceFieldValid from "../../Common/EmptySpaceFieldValid";
import {
  WARRANTY_PLAN_DRAFT_URL,
  WARRANTY_PLAN_HISTORY_URL,
} from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import ReasonDialog from "../common/ReasonDialog";
import { cancelPlanSchedule, createPlanDraft, listPlanTiers } from "../common/warrantyApi";
import { requestIdFor, settleRequestId } from "../common/clientRequest";
import { useWarrantyNav } from "../common/useWarrantyNav";
import "../warranty.css";

const STATE_LABEL = { live: "Live", scheduled: "Scheduled", past: "Past", cancelled: "Cancelled" };

export const PlaceholderBadge = () => (
  <span className="warranty-badge is-warning">
    Placeholder terms: cannot be sold in production
  </span>
);

export const StateBadge = ({ state }) => (
  <span className={`warranty-badge is-${state}`}>{STATE_LABEL[state] || state}</span>
);

const NewTierDialog = ({ show, onClose, onCreate }) => {
  const [tier, setTier] = useState("");
  const [tierError, setTierError] = useState("");
  const [touched, setTouched] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (show) {
      setTier("");
      setTierError("");
      setTouched(false);
      setInFlight(false);
      setFailure(null);
    }
  }, [show]);

  // CheckValid runs on every change, so an empty error with some text means the key is fine.
  const valid = tier !== "" && tierError === "";

  const change = (value) => {
    setTier(value);
    setFailure(null);
    CheckValid(value, { type: "warranty_tier_key", error: setTierError });
  };

  const create = async () => {
    setTouched(true);
    CheckValid(tier, { type: "warranty_tier_key", error: setTierError });
    if (!valid || inFlight) {
      return;
    }
    setInFlight(true);
    const result = await onCreate(tier);
    setInFlight(false);
    if (result?.ok !== true) {
      setFailure(result);
    }
  };

  return (
    <Modal
      show={show}
      onHide={inFlight ? () => {} : onClose}
      backdrop={inFlight ? "static" : true}
      keyboard={!inFlight}
      className="commonModal"
      centered
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>New tier</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="warranty-dialog-hint">
          Choose the tier key. It cannot be changed later. This starts an empty draft; nothing is
          sold until you publish it.
        </p>
        <div className="formloginset">
          <Form.Group className="mb-1" controlId="warrantyTierKey">
            <Form.Label>Tier key</Form.Label>
            <Form.Control
              type="text"
              value={tier}
              maxLength={32}
              onChange={(event) => change(event.target.value)}
              onBlur={() => setTouched(true)}
              onKeyDown={(event) => {
                EmptySpaceFieldValid(event);
                if (event.key === "Enter") {
                  create();
                }
              }}
              placeholder="for example assure_max"
              disabled={inFlight}
              autoFocus
            />
            {(touched || tier !== "") && tierError && <div className="danger-colorset">{tierError}</div>}
          </Form.Group>
        </div>
        {failure && (
          <div className="warranty-dialog-error" role="alert">
            {failure.message}
            {failure.draftId && (
              <>
                {" "}
                <Link to={`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(failure.draftId)}`}>Open that draft</Link>
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={inFlight}>
          Cancel
        </Button>
        <Button className="addbtncmn" onClick={create} disabled={!valid || inFlight}>
          {inFlight ? "Creating" : "Create draft"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const TierCard = ({ tier, busy, starting, error, onNewDraft, onCancelSchedule }) => {
  const { live, scheduled, draft, sales } = tier;
  // A tier with no live version starts a new draft from its last scheduled version.
  const lastScheduled = scheduled.length > 0 ? scheduled[scheduled.length - 1] : null;
  const canStartDraft = live !== null || lastScheduled !== null;

  return (
    <article className="warranty-card" aria-label={`${tier.displayName} plan`}>
      <div className="warranty-card-head">
        <div>
          <h5>{tier.displayName || tier.tier}</h5>
          <small>
            {tier.tier} / rank {tier.tierRank}
          </small>
        </div>
      </div>

      <div className="warranty-block">
        <h6>Live version</h6>
        {live ? (
          <>
            <div className="warranty-line">
              <span>
                Terms <strong>{live.termsVersion}</strong>
              </span>
              <StateBadge state="live" />
            </div>
            <div className="warranty-muted">
              Live since {CommonDateTime(live.effectiveFrom, "LL", "show")}
            </div>
            {live.placeholderTerms && (
              <div className="mt-1">
                <PlaceholderBadge />
              </div>
            )}
          </>
        ) : (
          <div className="warranty-muted">No live version yet.</div>
        )}
      </div>

      <div className="warranty-block">
        <h6>Scheduled</h6>
        {scheduled.length === 0 ? (
          <div className="warranty-muted">Nothing scheduled.</div>
        ) : (
          scheduled.map((version) => (
            <div className="warranty-line" key={version.id}>
              <span>
                <strong>{version.termsVersion}</strong> starts{" "}
                {CommonDateTime(version.effectiveFrom, "LL", "show")}
                {version.placeholderTerms && (
                  <>
                    {" "}
                    <span className="warranty-badge is-warning">Placeholder terms</span>
                  </>
                )}
              </span>
              <button
                type="button"
                className="warranty-inline-btn is-danger"
                onClick={() => onCancelSchedule(version, tier)}
                disabled={busy}
                aria-label={`Cancel schedule of ${version.termsVersion} for ${tier.displayName}`}
              >
                Cancel schedule
              </button>
            </div>
          ))
        )}
      </div>

      <div className="warranty-block">
        <h6>Draft</h6>
        {draft ? (
          <div className="warranty-line">
            <span>
              Updated by {draft.updatedBy}, {CommonDateTime(draft.updatedAt, "LL, h:mm A", "show")}
            </span>
            {draft.stale && <span className="warranty-badge is-stale">Stale</span>}
          </div>
        ) : (
          <div className="warranty-muted">No open draft.</div>
        )}
      </div>

      <div className="warranty-block">
        <h6>Sales</h6>
        <div>
          {sales.inForce} in force, {sales.soldLast30Days} sold in the last 30 days
        </div>
      </div>

      {error && (
        <div className="warranty-dialog-error" role="alert">
          {error}
        </div>
      )}

      <div className="warranty-card-actions">
        {draft ? (
          <Link
            className="warranty-inline-btn is-primary"
            to={`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(draft.id)}`}
            aria-label={`Continue the draft of ${tier.displayName}`}
          >
            Continue
          </Link>
        ) : (
          canStartDraft && (
            <button
              type="button"
              className="warranty-inline-btn is-primary"
              onClick={() => onNewDraft(tier, lastScheduled)}
              disabled={busy}
            >
              {starting ? "Starting" : live ? "New draft from live" : "New draft from scheduled"}
            </button>
          )
        )}
        <Link
          className="warranty-inline-btn"
          to={`${WARRANTY_PLAN_HISTORY_URL}/${encodeURIComponent(tier.tier)}`}
          aria-label={`Version history of ${tier.displayName}`}
        >
          Version history
        </Link>
      </div>
    </article>
  );
};

const PlansHome = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const navigate = useNavigate();
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.PLANS_ADMIN);
  useWarrantyNav(allowed);

  const [view, setView] = useState({ status: "loading", data: null, message: "" });
  const [reloading, setReloading] = useState(false);
  const [busyTier, setBusyTier] = useState("");
  const [cardErrors, setCardErrors] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showNewTier, setShowNewTier] = useState(false);
  const mounted = useRef(true);
  const cancelIdRef = useRef(null);
  const createIdRef = useRef(null);
  const newTierIdRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const result = await listPlanTiers(accessToken);
    if (!mounted.current) {
      return;
    }
    if (result.ok) {
      setView({ status: "ready", data: result.data, message: "" });
    } else {
      setView({ status: "error", data: null, message: result.message });
    }
  }, [accessToken]);

  useEffect(() => {
    if (allowed) {
      load();
    }
  }, [allowed, load]);

  const retry = async () => {
    setReloading(true);
    await load();
    if (mounted.current) {
      setReloading(false);
    }
  };

  const newDraft = async (tier, lastScheduled) => {
    setBusyTier(tier.tier);
    setCardErrors((errors) => ({ ...errors, [tier.tier]: "" }));
    const body = {
      tier: tier.tier,
      basedOnPlanVersionId: tier.live ? null : lastScheduled.id,
    };
    const result = await createPlanDraft(
      accessToken,
      body,
      requestIdFor(createIdRef, `create:${JSON.stringify(body)}`)
    );
    settleRequestId(createIdRef, result);
    if (!mounted.current) {
      return;
    }
    setBusyTier("");
    if (result.ok) {
      navigate(`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(result.data.id)}`);
    } else {
      setCardErrors((errors) => ({ ...errors, [tier.tier]: result.message }));
      if (result.code === "PLAN_DRAFT_EXISTS") {
        // Someone opened a draft since this list was loaded.
        load();
      }
    }
  };

  const confirmCancel = async (reason) => {
    const id = requestIdFor(cancelIdRef, `cancel:${cancelTarget.version.id}:${reason}`);
    const result = await cancelPlanSchedule(accessToken, cancelTarget.version.id, reason, id);
    settleRequestId(cancelIdRef, result);
    if (!mounted.current) {
      return { ok: true };
    }
    if (result.ok) {
      toast.success("The scheduled version was cancelled.");
      load();
      return { ok: true };
    }
    if (result.code === "PLAN_VERSION_NOT_SCHEDULED") {
      load();
    }
    return { ok: false, message: result.message };
  };

  const createTier = async (tier) => {
    const body = { tier, empty: true, basedOnPlanVersionId: null };
    const result = await createPlanDraft(
      accessToken,
      body,
      requestIdFor(newTierIdRef, `new:${tier}`)
    );
    settleRequestId(newTierIdRef, result);
    if (result.ok) {
      navigate(`${WARRANTY_PLAN_DRAFT_URL}/${encodeURIComponent(result.data.id)}`);
      return { ok: true };
    }
    return { ok: false, message: result.message, draftId: result.details?.draftId };
  };

  if (!allowed) {
    return (
      <section className="Main_section">
        <div className="warranty-page">
          <EmptyState title="You do not have access to Assure plans">
            <span>Ask an admin to give you the Warranty Assure Plans permission.</span>
          </EmptyState>
        </div>
      </section>
    );
  }

  const tiers = view.data?.tiers || [];

  return (
    <section className="Main_section">
      <CommonScrollBar className={"ScrollbarsSidebar"} height={{ height: "calc(100vh - 90px)" }}>
        <div className="warranty-page">
          <div className="warranty-page-head">
            <div>
              <h4>Assure plans</h4>
              <p className="warranty-sub">
                {view.data
                  ? `Market ${view.data.country}, today is ${CommonDateTime(view.data.today, "LL", "show")}`
                  : "Market IN"}
              </p>
            </div>
            <div className="warranty-page-actions">
              <Button
                className="Addbtn"
                onClick={() => setShowNewTier(true)}
                disabled={view.status !== "ready"}
              >
                New tier
              </Button>
            </div>
          </div>

          {view.status === "loading" && <Loading label="Loading plans" />}
          {view.status === "error" && (
            <ErrorState message={view.message} onRetry={retry} retrying={reloading} />
          )}
          {view.status === "ready" && tiers.length === 0 && (
            <EmptyState title="No plans yet">
              <span>Use New tier to start the first draft.</span>
            </EmptyState>
          )}
          {view.status === "ready" && tiers.length > 0 && (
            <div className="warranty-tier-grid">
              {tiers.map((tier) => (
                <TierCard
                  key={tier.tier}
                  tier={tier}
                  busy={busyTier !== ""}
                  starting={busyTier === tier.tier}
                  error={cardErrors[tier.tier]}
                  onNewDraft={newDraft}
                  onCancelSchedule={(version, tierView) =>
                    setCancelTarget({ version, tier: tierView })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </CommonScrollBar>

      <ReasonDialog
        show={cancelTarget !== null}
        title="Cancel scheduled version"
        explanation={
          cancelTarget
            ? `This stops ${cancelTarget.version.termsVersion} of ${cancelTarget.tier.displayName} from starting on ${CommonDateTime(
                cancelTarget.version.effectiveFrom,
                "LL",
                "show"
              )}. The date stays taken. Nothing already sold changes.`
            : ""
        }
        confirmLabel="Cancel schedule"
        onConfirm={confirmCancel}
        onClose={() => setCancelTarget(null)}
      />
      <NewTierDialog
        show={showNewTier}
        onClose={() => setShowNewTier(false)}
        onCreate={createTier}
      />
    </section>
  );
};

export default PlansHome;
